import { useEffect, useState, useCallback } from "react";
import { api } from "../api";

/**
 * The caller's personal skill library — owned + shared-with-them. Each skill
 * carries the caller's per-user `is_active` flag (independent of other users'
 * toggles for shared skills).
 *
 * Active skills load into every new chat the caller starts, regardless of
 * which environment the chat lives in.
 */
export function useSkills() {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.listSkills();
      setSkills(list);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const getSkill = useCallback(async (skillId) => api.getSkill(skillId), []);

  const createSkill = useCallback(async (payload) => {
    const s = await api.createSkill(payload);
    await refresh();
    return s;
  }, [refresh]);

  // Upload a .zip bundle (multi-file skill). Mirrors createSkill: server
  // creates the skill, then we refresh so the new row appears. Throws on
  // failure so the caller can surface the message (e.g. "exactly one
  // root SKILL.md file").
  const uploadSkillZip = useCallback(async (file) => {
    const s = await api.uploadSkillZip(file);
    await refresh();
    return s;
  }, [refresh]);

  const patchSkill = useCallback(async (skillId, patch) => {
    const s = await api.patchSkill(skillId, patch);
    await refresh();
    return s;
  }, [refresh]);

  const toggleSkill = useCallback(async (skillId, isActive) => {
    // Optimistic flip — roll back on error so the checkbox stays in sync.
    setSkills((prev) => prev.map(
      (s) => s._id === skillId ? { ...s, is_active: !!isActive } : s
    ));
    try {
      await api.setSkillActive(skillId, !!isActive);
    } catch (e) {
      setSkills((prev) => prev.map(
        (s) => s._id === skillId ? { ...s, is_active: !isActive } : s
      ));
      throw e;
    }
  }, []);

  const deleteSkill = useCallback(async (skillId) => {
    await api.deleteSkill(skillId);
    await refresh();
  }, [refresh]);

  const activeCount = skills.filter((s) => s.is_active).length;

  return {
    skills, activeCount, loading, error, refresh,
    getSkill, createSkill, uploadSkillZip, patchSkill, toggleSkill, deleteSkill,
  };
}
